import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowUpRight, Footprints } from "lucide-react";
import { NUDGES, GUIDE } from "@/data/portfolio";
import GuideIcon from "@/components/site/GuideIcon";
import { track } from "@/lib/analytics";
import { useLang } from "@/lib/i18n";

const LINGER_MS = 20000;
const AUTO_HIDE_MS = 18000;
const seenKey = (id) => `sinthusiast_nudge_${id}`;

export function useLingerNudge(enabled) {
  const [nudge, setNudge] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!enabled || pathname !== "/") return;
    const timers = new Map();
    const arm = (id) => {
      if (timers.has(id) || sessionStorage.getItem(seenKey(id)) || !NUDGES[id]) return;
      timers.set(
        id,
        setTimeout(() => {
          sessionStorage.setItem(seenKey(id), "1");
          setNudge({ id, ...NUDGES[id] });
          track("nudge_shown", { section: id });
        }, LINGER_MS)
      );
    };
    const disarm = (id) => {
      clearTimeout(timers.get(id));
      timers.delete(id);
    };
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) =>
          e.isIntersecting ? arm(e.target.id) : disarm(e.target.id)
        ),
      { threshold: 0.5 }
    );
    const mount = setTimeout(
      () => document.querySelectorAll("main section[id]").forEach((s) => io.observe(s)),
      300
    );
    return () => {
      clearTimeout(mount);
      timers.forEach((t) => clearTimeout(t));
      io.disconnect();
    };
  }, [enabled, pathname]);

  useEffect(() => {
    if (!nudge) return;
    const t = setTimeout(() => setNudge(null), AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [nudge]);

  const show = (id) => {
    if (!NUDGES[id]) return;
    sessionStorage.setItem(seenKey(id), "1");
    setNudge({ id, ...NUDGES[id] });
    track("nudge_shown", { section: id, intro: true });
  };

  return [nudge, () => setNudge(null), show];
}

export function useSectionIntro(id, ready, onShow) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!ready || pathname !== "/" || sessionStorage.getItem(seenKey(id))) return;
    let io;
    const mount = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      io = new IntersectionObserver(
        ([e]) => {
          if (!e.isIntersecting) return;
          io.disconnect();
          onShow(id);
        },
        { threshold: 0.6 }
      );
      io.observe(el);
    }, 1600);
    return () => {
      clearTimeout(mount);
      io?.disconnect();
    };
  }, [id, ready, pathname]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function NudgeBubble({ nudge, onClose }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const go = () => {
    track("nudge_click", { section: nudge.id });
    onClose();
    if (nudge.to.startsWith("#")) {
      document.getElementById(nudge.to.slice(1))?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(nudge.to);
    }
  };
  return (
    <div
      data-testid="guide-nudge"
      data-section={nudge.id}
      className="fixed bottom-24 right-6 z-50 w-[min(92vw,320px)] p-4 bg-white border border-[#e5e0d8] shadow-2xl rounded-2xl fade-up"
    >
      <button
        data-testid="guide-nudge-dismiss"
        onClick={onClose}
        className="absolute top-2.5 right-2.5 h-7 w-7 grid place-items-center rounded-full hover:bg-[#f2ece4]"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-3">
        <span className="h-8 w-8 shrink-0 rounded-full btn-guide grid place-items-center">
          <GuideIcon className="h-5 w-5" />
        </span>
        <div className="pr-4">
          <div className="font-mono-label text-[8px] text-[#86928a]">{GUIDE.name} · {t("tip")}</div>
          <p data-testid="guide-nudge-text" className="mt-1 text-sm text-[#14352a] leading-relaxed">
            {t(nudge.text)}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
            {nudge.walk && (
              <button
                data-testid="guide-nudge-walk"
                onClick={() => {
                  track("nudge_click", { section: nudge.id, walk: true });
                  onClose();
                  document.getElementById("journey")?.scrollIntoView({ behavior: "smooth" });
                  setTimeout(() => window.dispatchEvent(new CustomEvent("sinthusiast:walk")), 500);
                }}
                className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full btn-guide text-xs font-medium"
              >
                <Footprints className="h-3.5 w-3.5" /> {t(nudge.walk)}
              </button>
            )}
            <button
              data-testid="guide-nudge-cta"
              onClick={go}
              className="inline-flex items-center gap-1 font-mono-label text-[9px] text-[#9a7218] link-underline"
            >
              {t(nudge.cta)} <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
